/**
 * documentDedupe — collapse revision-archive duplicates in the
 * appendix's Documents list.
 *
 * Symptom this fixes: a 4-page Documents appendix with 7 entries for
 * "AZR Purchase Option Agreement Kenny Nichols (Redhead)" with date
 * suffixes — the same document at different revision points. The
 * dedupe is a render-time projection; we never mutate the underlying
 * `site.documents` array.
 *
 * Algorithm:
 *
 *   1. Canonicalize each document's filename: strip the path, drop
 *      the extension, strip parenthetical quality / date markers
 *      (Clean / Redline / Final / Draft + date), strip date and
 *      version suffixes that follow the basename, trim, lowercase.
 *
 *   2. Compute a folder family by stripping the "z. Archive" prefix
 *      so archived revisions group with active revisions.
 *
 *   3. Group by `${folderFamily}::${canonical}`. Within each group,
 *      pick the "latest" using a deterministic tiebreak chain:
 *
 *        a. Highest quality marker wins (Final > Clean > Redline >
 *           Draft). The quality tier comes from the parenthetical
 *           the user wrote into the filename.
 *        b. Latest `uploadedAt` ISO string wins next.
 *        c. As a final tiebreak, prefer the document that's NOT in
 *           a "z. Archive" folder so the active copy beats the
 *           archived copy at the same upload time.
 *
 *   4. Earlier revisions sort newest-first inside `revisionsHidden`
 *      so the expander's "+N earlier" chip lists most-recent-first.
 */
import type { SiteDocument } from '../types/siteRag';
import { resolveSiteDocumentNameInfo } from './site/resolveSiteDocuments';

export interface DedupedDocGroup {
  /** The chosen "current" doc for this canonical group. */
  latest: SiteDocument;
  /** The other entries in the group, newest first. Empty when no duplicates. */
  revisionsHidden: SiteDocument[];
}

/* ── Pattern set ──────────────────────────────────────────────── */

const ARCHIVE_FOLDER_PREFIX_RE = /^z\.?\s*archive\b\/*/i;
const QUALITY_PAREN_RE = /\s*\((?:final|clean|redline|draft)[^)]*\)/gi;
const DATE_SUFFIX_RE = /\s+\d{4}[.\-]\d{1,2}[.\-]\d{1,2}\b/g;
const VERSION_SUFFIX_RE = /\s+v\d+(?:\.\d+)*\b/gi;

/** -1 = absent; higher = better. Final > Clean > Redline > Draft. */
function qualityRank(fileName: string | undefined): number {
  const safe = typeof fileName === 'string' ? fileName : '';
  if (/\(final\b/i.test(safe)) return 3;
  if (/\(clean\b/i.test(safe)) return 2;
  if (/\(redline\b/i.test(safe)) return 1;
  if (/\(draft\b/i.test(safe)) return 0;
  return -1;
}

/** Drop path components from a fileName that may carry slashes. */
function basename(fileName: string | undefined): string {
  const safe = typeof fileName === 'string' ? fileName : '';
  const idx = safe.lastIndexOf('/');
  return idx >= 0 ? safe.slice(idx + 1) : safe;
}

/** Drop the extension (everything after the LAST `.`). Returns name + ext separately. */
function splitExt(name: string): { stem: string; ext: string } {
  const idx = name.lastIndexOf('.');
  if (idx <= 0) return { stem: name, ext: '' };
  return { stem: name.slice(0, idx), ext: name.slice(idx) };
}

/**
 * Compute the canonical lowercase grouping key for a single fileName.
 * Strips path / quality markers / date suffixes / version suffixes
 * so revisions of the same logical document collapse to one key.
 *
 * Exported for the unit tests; the appendix wiring goes through
 * `dedupeDocsByCanonicalName` instead.
 */
export function canonicalName(fileName: string | undefined): string {
  const stem = splitExt(basename(fileName)).stem;
  const stripped = stem
    .replace(QUALITY_PAREN_RE, '')
    .replace(DATE_SUFFIX_RE, '')
    .replace(VERSION_SUFFIX_RE, '')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped.toLowerCase();
}

/**
 * Compute the folder family — the doc's `folder` (or empty string)
 * with the "z. Archive" prefix dropped. Used to group archived and
 * active copies of the same document under the same key.
 */
export function folderFamily(folder: string | undefined): string {
  if (!folder) return '';
  return folder.replace(ARCHIVE_FOLDER_PREFIX_RE, '').trim().toLowerCase();
}

function isArchived(doc: SiteDocument): boolean {
  if (!doc.folder) return false;
  return ARCHIVE_FOLDER_PREFIX_RE.test(doc.folder);
}

/** Sort comparator — descending "newness" so .sort() puts the latest first. */
function compareNewerFirst(a: SiteDocument, b: SiteDocument): number {
  const aInfo = resolveSiteDocumentNameInfo(a, a.id);
  const bInfo = resolveSiteDocumentNameInfo(b, b.id);
  const qa = aInfo.isFallbackIdentity ? -1 : qualityRank(aInfo.fileName);
  const qb = bInfo.isFallbackIdentity ? -1 : qualityRank(bInfo.fileName);
  if (qa !== qb) return qb - qa;
  const ta = a.uploadedAt ?? '';
  const tb = b.uploadedAt ?? '';
  if (ta !== tb) return ta < tb ? 1 : -1;
  // Active copies beat archived copies at otherwise-equal newness so
  // the visible row is the one a user can act on.
  const aArchived = isArchived(a);
  const bArchived = isArchived(b);
  if (aArchived !== bArchived) return aArchived ? 1 : -1;
  return 0;
}

/**
 * Group documents by canonical name + folder family, pick the latest
 * within each group, return one `DedupedDocGroup` per group.
 *
 * Group order in the returned array preserves the relative position
 * of each canonical name's FIRST appearance in the input — so callers
 * that pre-sort by relevance / inclusion / etc. keep that ordering.
 */
export function dedupeDocsByCanonicalName(docs: SiteDocument[]): DedupedDocGroup[] {
  const groups = new Map<string, SiteDocument[]>();
  const order: string[] = [];

  for (let idx = 0; idx < docs.length; idx += 1) {
    const d = docs[idx]!;
    const nameInfo = resolveSiteDocumentNameInfo(d, `doc-${idx}`);
    const key = nameInfo.isFallbackIdentity
      ? `${folderFamily(d.folder)}::id:${nameInfo.fileName}`
      : `${folderFamily(d.folder)}::name:${canonicalName(nameInfo.fileName) || nameInfo.fileName.toLowerCase()}`;
    const list = groups.get(key);
    if (list) {
      list.push(d);
    } else {
      groups.set(key, [d]);
      order.push(key);
    }
  }

  return order.map((key) => {
    const list = [...(groups.get(key) ?? [])].sort(compareNewerFirst);
    const [latest, ...revisionsHidden] = list;
    return { latest: latest!, revisionsHidden };
  });
}

/**
 * Convenience: total count of revisions that would be hidden under
 * the deduped projection. Drives the "{M} earlier revisions hidden"
 * chip in the appendix without re-walking the groups.
 */
export function countHiddenRevisions(groups: DedupedDocGroup[]): number {
  let n = 0;
  for (const g of groups) n += g.revisionsHidden.length;
  return n;
}
